import React, { useEffect, useRef } from 'react';
import { Badge, Button, ListItemText, Menu, MenuItem, Stack } from '@mui/material';
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import FeatherIcon from 'feather-icons-react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../components/theme';
import { colours as constColours, CustomTooltip } from '../../consts';
import ReadAlertConfirmComponent from '../../pages/alerts/read-confirm';
import { AppDataContext } from '../../components/context/app-data-context';
import { THEME_MODES } from '../../helpers/constant';
import { removeDetectKeywordFromVisuals } from '../../helpers';
import { readAlertsAPI } from '../../services/alerts';

function AlertsMenuComponent() {
    const theme = useTheme();
    const { colours, FONT } = useThemeMode();
    const { currentCompanyAlerts, loadCompanyAlertsData, unreadCounts } = React.useContext(AppDataContext);

    const INITIAL_LOAD = 30;
    const navigate = useNavigate();
    const paperRef = useRef(null);
    const [bellElement, setBellElement] = React.useState();
    const alertMenuOpen = Boolean(bellElement);
    const [visibleRows, setVisibleRows] = React.useState([]);

    // this effect is used to load the alerts when the menu is opened
    useEffect(() => {
        if (bellElement) {
            if (paperRef.current) {
                paperRef.current.scrollTop = 0;
            }
            setVisibleRows([...currentCompanyAlerts.slice(0, INITIAL_LOAD)]);
        }
    }, [bellElement]);

    useEffect(() => {
        if (bellElement) {
            setVisibleRows((prevRows) => {
                const nextRows = currentCompanyAlerts.slice(0, prevRows.length);
                return [...nextRows];
            });
        }
    }, [currentCompanyAlerts, bellElement]);

    // this scroll event is used to load more alerts when the user scrolls to the bottom of the menu
    // it loads 30 more alerts at a time
    const handleScroll = (e) => {
        if ((e.target.scrollTop + e.target.clientHeight + 200) >= e.target.scrollHeight) {
            setVisibleRows((prevRows) => {
                const nextRows = currentCompanyAlerts.slice(prevRows.length, prevRows.length + INITIAL_LOAD);
                return [...prevRows, ...nextRows];
            });
        }
    }


    return (
        <React.Fragment>
            <CustomTooltip title="Alerts">
                <IconButton
                    id="basic-button"
                    aria-controls={alertMenuOpen ? "basic-menu-alert" : undefined}
                    aria-haspopup="true"
                    aria-expanded={alertMenuOpen ? "true" : undefined}
                    onClick={(e) => setBellElement(e.target)}
                    variant="transparent"
                    aria-label="Alerts"
                    color="inherit"
                    sx={(theme) => ({
                        backgroundColor: alertMenuOpen && (theme.palette.mode === THEME_MODES.LIGHT ? colours.grayDark500 : constColours.black)
                    })}
                >
                    <Badge
                        badgeContent={unreadCounts > 0 ? unreadCounts : null}
                        color="error"
                    >
                        <FeatherIcon icon="bell" />
                    </Badge>
                </IconButton>
            </CustomTooltip>
            <Menu
                slotProps={{
                    paper: {
                        ref: paperRef,
                        onScroll: handleScroll
                    }
                }}
                id="basic-menu-alert"
                anchorEl={bellElement}
                open={alertMenuOpen}
                onClose={() => {
                    setBellElement(null);
                    setVisibleRows([...currentCompanyAlerts.slice(0, INITIAL_LOAD)]);
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                sx={{ marginTop: 1 }}
                MenuListProps={{
                    "aria-labelledby": "basic-button",
                    disablePadding: true,
                    sx: {
                        width: "440px !important",
                        maxWidth: '100%'
                    }
                }}
            >
                <Stack
                    position="sticky"
                    top={0}
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                    component="li"
                    sx={{
                        backgroundColor: colours.white,
                        zIndex: 1,
                        borderBottom: `1px solid ${colours.grayDisabled}`,
                        padding: "1rem 1.1875rem 1rem 1.5rem",
                    }}
                >
                    <Typography
                        variant="h3"
                        fontWeight={600}
                        sx={{ fontSize: { xs: FONT.SIZE_24, md: FONT.SIZE_28 } }}
                    >
                        Alerts
                    </Typography>
                    <ReadAlertConfirmComponent
                        markAll={true}
                        unreadCounts={unreadCounts}
                        selected={currentCompanyAlerts.filter(item => !item.isRead).map((item) => item.id)} onSuccess={() => {
                            console.log("Alert marked as read");
                            loadCompanyAlertsData(false);
                        }} />
                </Stack>
                {visibleRows.length === 0 ? <MenuItem
                    key={"no-alerts"}
                    sx={{ padding: ".75rem 1.5rem" }}
                >
                    <ListItemText
                        primary={"No alerts available"}
                        secondary={"You will be notified here when you have new alerts."}
                        primaryTypographyProps={{
                            sx: {
                                fontWeight: 400,
                                whiteSpace: "normal"
                            }
                        }}
                        secondaryTypographyProps={{
                            sx: {
                                whiteSpace: "normal",
                                fontSize: FONT.SIZE_13
                            }
                        }}
                    />
                </MenuItem> : ""}
                {visibleRows?.map((z, zidx) => (
                    <MenuItem
                        key={zidx}
                        className={z.isRead ? "read" : "unread"}
                        onClick={() => {
                            navigate("/conversation?id=" + z.conversation);
                            setBellElement(null);
                            setVisibleRows([]);

                            if(z.isRead) return;
                            // mark read via API 
                            readAlertsAPI({ readAlertIds: [z.id] }).then(() => {
                                loadCompanyAlertsData(false);
                            }).catch((err) => {
                                console.error("Error marking alert as read: ", err);
                            });
                        }}
                        sx={{
                            padding: ".75rem 1.5rem",
                            marginBottom: .5,
                            gap: 1,
                            "&.unread": {
                                backgroundColor: theme.palette.primary.main + "1A",
                                "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    backgroundColor: theme.palette.primary.main,
                                    width: 4,
                                    height: "100%"
                                },
                                ".MuiListItemText-primary": {
                                    fontWeight: 600,
                                }
                            },
                        }}
                    >
                        <ListItemText
                            primary={removeDetectKeywordFromVisuals(z.name)}
                            secondary={"Detected in - " + z.campaign}
                            primaryTypographyProps={{
                                sx: {
                                    whiteSpace: "normal",
                                    wordBreak: "break-word"
                                }
                            }}
                            secondaryTypographyProps={{
                                sx: {
                                    fontSize: FONT.SIZE_13,
                                    whiteSpace: "normal",
                                    wordBreak: "break-word"
                                }
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', fontSize: FONT.SIZE_13, flexShrink: 0 }}
                        >
                            {moment(z.createdAt).fromNow()}
                        </Typography>
                    </MenuItem>
                ))}
                <Stack
                    position="sticky"
                    bottom={0}
                    flexDirection="row"
                    justifyContent="center"
                    alignItems="center"
                    gap={1}
                    component="li"
                    sx={{
                        backgroundColor: colours.white,
                        zIndex: 1,
                        borderTop: `1px solid ${colours.grayDisabled}`,
                    }}
                >
                    <Button
                        variant="text"
                        onClick={() => {
                            navigate("/alerts");
                            setBellElement(null);
                            setVisibleRows([]);
                        }}
                        endIcon={<FeatherIcon icon="arrow-right" />}
                        sx={{ fontSize: FONT.SIZE_16, fontWeight: 600, padding: ".75rem 1rem", width: "100%" }}
                    >
                        Go to Alerts Page
                    </Button>
                </Stack>
            </Menu>
        </React.Fragment>
    )
}

export default AlertsMenuComponent;